import { useState, forwardRef, useEffect } from 'react';
import { DateRangePicker, CustomProvider } from 'rsuite';
import arEG from 'rsuite/locales/ar_EG';
import enUS from 'rsuite/locales/en_US';
import type { IDateRangePickerMenu } from "./../../../types/date_range_picker/menu";
import { getDir, getHorizontalPlacement, getVerticalPlacement } from './../../../utils/getPlacement';
import { Button } from "../../Button";
import 'rsuite/DateRangePicker/styles/index.css';
import "./rsuite-range-picker.scss";
import styles from "./date-range-picker-menu.module.scss";

export const DateRangePickerMenu = forwardRef<HTMLDivElement, IDateRangePickerMenu>((
  {
    lang = 'en',
    id,
    open = false,
    dateRange,
    disabledDate,
    onSelectDate,
    customLocale,
    showOneCalendar = true,
    horizontalPlacement = 'start',
    verticalPlacement = 'bottom',
    preDefinedRanges,
    actionBtnsProps = [],
    jumpToStartDateOnShortcutClick = false,
    rangePickerBtnRef,
    containerRef
  },
  ref
) => {

  const autoHorizontalPlacement = typeof rangePickerBtnRef?.current === 'object' && getHorizontalPlacement(rangePickerBtnRef, getDir());
  const autoVerticalPlacement = typeof rangePickerBtnRef?.current === 'object' && getVerticalPlacement(rangePickerBtnRef);

  const [key, setKey] = useState(0);

  useEffect(() => {
    // To force the range picker to return to the start date, as it doesn't provide control over this via a prop
    jumpToStartDateOnShortcutClick && dateRange && setKey(Date.now());
  }, [dateRange]);

  return (
    open &&
    <div className={styles["date-range-menu"]}
      id={id}
      ref={ref}
      data-drp-menu-horizontal={
        horizontalPlacement === 'auto' ?
          (
            autoHorizontalPlacement ?
              autoHorizontalPlacement
              :
              'start'
          )
          :
          horizontalPlacement
      }
      data-drp-menu-vertical={
        verticalPlacement === 'auto' ?
          (
            autoVerticalPlacement ?
              autoVerticalPlacement
              :
              'bottom'
          )
          :
          verticalPlacement
      }
    >

      {
        preDefinedRanges &&
        <div className={styles["date-range-menu__pre-defined-ranges"]}>
          {
            preDefinedRanges.map((range, i: number) =>
              <div
                key={`pre-selected-range-${i}`}
                className={`
                  ${styles["date-range-menu__pre-defined-ranges__range"]} 
                ${range.selected ? styles['date-range-menu__pre-defined-ranges__range--selected'] : ''}
                ${range.disabled ? styles['date-range-menu__pre-defined-ranges__range--disabled'] : ''}
                `}
                onClick={() => range.onClickHandler && range.onClickHandler(range.index, range.selectionRange, range.disabledRange)}
              >
                {range.label}
              </div>
            )
          }
        </div>
      }

      <div className={styles["date-range-menu__picker-actions-wrapper"]}>

        <div className={styles["date-range-menu__picker-actions-wrapper__picker"]}>
          <CustomProvider locale={lang === 'en' ? enUS : arEG}>
            <DateRangePicker
              key={`custom-drp-${key}`}
              id={id}
              showHeader={false}
              open={open}
              showOneCalendar={showOneCalendar}
              shouldDisableDate={disabledDate}
              ranges={[]}
              menuStyle={{ boxShadow: "none", borderRadius: "0" }}
              value={dateRange}
              calendarSnapping
              format="yyyy-MMM-dd"
              locale={customLocale}
              onSelect={(value) => onSelectDate(value)}
              container={containerRef?.current || undefined}
            />
          </CustomProvider>
        </div>

        <div className={styles["date-range-menu__picker-actions-wrapper__actions"]}>
          {
            actionBtnsProps.map((btn, i: number) =>
              <Button
                key={`drp-action-btn-${i}`}
                size={btn.size}
                color={btn.color}
                variant={btn.variant}
                text={btn.text}
                disabled={btn.disabled}
                onClickHandler={btn.onClickHandler}
              />
            )
          }
        </div>
      </div>
    </div>
  )
});